import { NgZone } from "@angular/core";
import { NgForage } from "ngforage";
import { EnvironmentConfiguration } from '@nts/std/environments';
import { Message } from "./tab-socket";
import { TabSocket } from "./tab-socket/tab-socket";
import {
    BarrierValuesInterface,
    CrossSiteStorageParamsInterface,
    GetStorageItemParamsInterface,
    MessageInterface,
    OverrideBarrierValuesInterface,
    RemoveStorageItemParamsInterface,
    SetCrossSiteStorageParamsInterface,
    SetStorageItemParamsInterface,
    StorageKeyExtensionParams,
    WriteDomainStorageParamsInterface
} from "@nts/std/interfaces";
import { MessageType, SerializabledDataType } from "@nts/std/types";

export class LocalstorageHelper {
    socket: TabSocket | null = null;
    socketListener: TabSocket | null = null;

    static #instance: LocalstorageHelper;
    private tenantId: number | null = null;
    private enterpriseId: number | null = null;
    private companyId: number | null = null;
    private userId: number | null = null;

    constructor(
        private readonly zone: NgZone,
        private readonly inIframe: boolean,
        private readonly ngf: NgForage,
        private readonly env: EnvironmentConfiguration,
        private readonly mockMode = false,
    ) {
        if (inIframe) {
            this.socket = new TabSocket(zone, window.parent, 'storage');
        }
    }

    static setCurrentTenantId(tenantId: number) {
        if (this.#instance != null) {
            this.#instance.tenantId = tenantId;
        }
    }

    static setCurrentEnterpriseId(enterpriseId: number) {
        if (this.#instance != null) {
            this.#instance.enterpriseId = enterpriseId;
        }
    }

    static setCurrentCompanyId(companyId: number) {
        if (this.#instance != null) {
            this.#instance.companyId = companyId;
        }
    }

    static setCurrentUserId(userId: number) {
        if (this.#instance != null) {
            this.#instance.userId = userId;
        }
    }

    static init(zone: NgZone, inIframe: boolean, ngf: NgForage, env: EnvironmentConfiguration, mockMode: boolean = false) {
        if (this.#instance == null) {
            this.#instance = new LocalstorageHelper(zone, inIframe, ngf, env, mockMode);
        }
    }

    static async clearStorage(
        domain: string | null = null
    ) {
        if (this.#instance?.inIframe) {
            // TODO
        } else if (domain != null) {
            // TODO
        } else {
            await this.#instance.ngf.clear();
        }
    }

    static async setStorageItem(
        params: SetStorageItemParamsInterface
    ): Promise<boolean>;

    /**
     * @deprecated Dalla versione REL_10.0.x escluso utilizzare "Named Parameters" seguendo SetStorageItemParamsInterface.
     */
    static async setStorageItem(
        key: string,
        value?: SerializabledDataType,
        domain?: string | null,
        tenantBarrier?: boolean,
        enterpriseBarrier?: boolean,
        userBarrier?: boolean,
        overrideBarrierValues?: OverrideBarrierValuesInterface
    ): Promise<boolean>;

    static async setStorageItem(
        keyOrOpts: string | SetStorageItemParamsInterface,
        value: SerializabledDataType | null = null,
        domain: string | null = null,
        tenantBarrier: boolean = true,
        enterpriseBarrier: boolean = true,
        userBarrier: boolean = false,
        overrideBarrierValues: OverrideBarrierValuesInterface = {
            tenantBarrierValue: undefined,
            enterpriseBarrierValue: undefined,
            companyBarrierValue: undefined,
            userBarrierValue: undefined
        }
    ): Promise<boolean> {

        // Destructuring sicuro all'inizio con type narrowing
        let finalKey: string;
        let finalValue = value ?? null;
        let finalDomain = domain ?? null;
        let finalTenantBarrier = tenantBarrier ?? true;
        let finalEnterpriseBarrier = enterpriseBarrier ?? true;
        let finalCompanyBarrier = false;
        let finalUserBarrier = userBarrier ?? false;
        let finalOverrideBarrierValues = overrideBarrierValues ?? {
            tenantBarrierValue: undefined,
            enterpriseBarrierValue: undefined,
            companyBarrierValue: undefined,
            userBarrierValue: undefined
        };

        if (typeof keyOrOpts !== 'string') {
            const opts = keyOrOpts;
            finalKey = opts.key;
            finalValue = opts.value ?? finalValue;
            finalDomain = opts.domain ?? finalDomain;
            finalTenantBarrier = opts.tenantBarrier ?? finalTenantBarrier;
            finalEnterpriseBarrier = opts.enterpriseBarrier ?? finalEnterpriseBarrier;
            finalCompanyBarrier = opts.companyBarrier ?? false;
            finalUserBarrier = opts.userBarrier ?? finalUserBarrier;
            finalOverrideBarrierValues = opts.overrideBarrierValues ?? finalOverrideBarrierValues;
        } else {
            finalKey = keyOrOpts;
        }

        if (this.#instance?.inIframe) {
            if (!finalDomain) {
                finalDomain = window.location.hostname;
            }

            await this.#updateCrossSiteStorageCache(
                {
                    key: finalKey,
                    domain: finalDomain,
                    tenantBarrier: finalTenantBarrier,
                    enterpriseBarrier: finalEnterpriseBarrier,
                    companyBarrier: finalCompanyBarrier,
                    userBarrier: finalUserBarrier,
                    overrideBarrierValues: finalOverrideBarrierValues,
                },
                new Message(finalValue)
            );

            // La REL_6_x.x non supporta companyBarrier, lo mettiamo nella "key"
            // TODO: rimuovere quando la DashboardMS sara' aggiornata
            if (finalCompanyBarrier) {
                const { companyId } = this.#getBarrierValues(finalOverrideBarrierValues);

                finalKey = this.#extendStorageKey({
                    key: finalKey,
                    companyId,
                });
            }

            const responseMessage: Message<boolean | undefined> | undefined =
                await this.#instance?.socket?.sendWithConfirmAsync<
                    SetCrossSiteStorageParamsInterface<SerializabledDataType>,
                    boolean
                >(
                    {
                        key: finalKey,
                        value: finalValue,
                        domain: finalDomain,
                        tenantBarrier: finalTenantBarrier,
                        enterpriseBarrier: finalEnterpriseBarrier,
                        companyBarrier: finalCompanyBarrier,
                        userBarrier: finalUserBarrier,
                        overrideBarrierValues: finalOverrideBarrierValues
                    },
                    // Quando si leggono/scrivono messaggi cross site anche se l'operazione è locale può essere necessario diverso tempo per recuperare delle chiavi salvate, dipende da quanto è grosso il dato da passare
                    30000,
                    MessageType.CrossSiteStorageSet,
                ).catch((err) => {
                    return new Message(false);
                });

            return responseMessage?.data ?? false;
        } else if (finalDomain != null) {
            const store: SerializabledDataType = await this.#readDomainStorage({
                key: finalKey,
                domain: finalDomain,
                tenantBarrier: finalTenantBarrier,
                enterpriseBarrier: finalEnterpriseBarrier,
                companyBarrier: finalCompanyBarrier,
                userBarrier: finalUserBarrier,
                overrideBarrierValues: finalOverrideBarrierValues
            });

            store[finalKey] = finalValue;

            return await this.#writeDomainStorage({
                domain: finalDomain,
                store,
                tenantBarrier: finalTenantBarrier,
                enterpriseBarrier: finalEnterpriseBarrier,
                companyBarrier: finalCompanyBarrier,
                userBarrier: finalUserBarrier,
                overrideBarrierValues: finalOverrideBarrierValues
            });
        } else {
            let result = true;

            const {
                tenantId,
            } = this.#getBarrierValues(finalOverrideBarrierValues);

            const calculatedKey = this.#composeStorageKey({
                key: finalKey,
                tenantBarrier: finalTenantBarrier,
                enterpriseBarrier: finalEnterpriseBarrier,
                companyBarrier: finalCompanyBarrier,
                userBarrier: finalUserBarrier,
                overrideBarrierValues: finalOverrideBarrierValues
            });

            if (
                finalTenantBarrier &&
                tenantId > 0
            ) {
                if (this.#instance.mockMode) {
                    window.localStorage.setItem(calculatedKey, JSON.stringify(finalValue));
                } else {
                    await this.#instance.ngf.setItem(calculatedKey, finalValue).catch((err) => result = false);
                }
            } else {
                if (this.#instance.mockMode) {
                    window.localStorage.setItem(finalKey, JSON.stringify(finalValue));
                } else {
                    await this.#instance.ngf.setItem(finalKey, finalValue).catch((err) => result = false);
                }
            }

            return result;
        }
    }

    static async getStorageItem(
        params: GetStorageItemParamsInterface
    ): Promise<SerializabledDataType | null>;

    /**
     * @deprecated Dalla versione REL_10.0.x escluso utilizzare "Named Parameters" seguendo GetStorageItemParamsInterface.
     */
    static async getStorageItem(
        key: string,
        domain?: string | null,
        tenantBarrier?: boolean,
        enterpriseBarrier?: boolean,
        userBarrier?: boolean,
        overrideBarrierValues?: OverrideBarrierValuesInterface,
    ): Promise<SerializabledDataType | null>;

    static async getStorageItem(
        keyOrOpts: string | GetStorageItemParamsInterface,
        domain: string | null = null,
        tenantBarrier: boolean = true,
        enterpriseBarrier: boolean = true,
        userBarrier: boolean = false,
        overrideBarrierValues: OverrideBarrierValuesInterface = {
            tenantBarrierValue: undefined,
            enterpriseBarrierValue: undefined,
            companyBarrierValue: undefined,
            userBarrierValue: undefined
        },
    ): Promise<SerializabledDataType | null> {

        // Destructuring sicuro all'inizio con type narrowing
        let finalKey: string;
        let finalDomain = domain ?? null;
        let finalTenantBarrier = tenantBarrier ?? true;
        let finalEnterpriseBarrier = enterpriseBarrier ?? true;
        let finalCompanyBarrier = false;
        let finalUserBarrier = userBarrier ?? false;
        let finalOverrideBarrierValues = overrideBarrierValues ?? {
            tenantBarrierValue: undefined,
            enterpriseBarrierValue: undefined,
            companyBarrierValue: undefined,
            userBarrierValue: undefined
        };

        if (typeof keyOrOpts !== 'string') {
            const opts = keyOrOpts;
            finalKey = opts.key;
            finalDomain = opts.domain ?? finalDomain;
            finalTenantBarrier = opts.tenantBarrier ?? finalTenantBarrier;
            finalEnterpriseBarrier = opts.enterpriseBarrier ?? finalEnterpriseBarrier;
            finalCompanyBarrier = opts.companyBarrier ?? false;
            finalUserBarrier = opts.userBarrier ?? finalUserBarrier;
            finalOverrideBarrierValues = opts.overrideBarrierValues ?? finalOverrideBarrierValues;
        } else {
            finalKey = keyOrOpts;
        }

        if (this.#instance?.inIframe) {
            if (!finalDomain) {
                finalDomain = window.location.hostname;
            }

            const messageCached: Message<SerializabledDataType | null> | undefined = await this.#getCrossSiteStorageCache({
                key: finalKey,
                domain: finalDomain,
                tenantBarrier: finalTenantBarrier,
                enterpriseBarrier: finalEnterpriseBarrier,
                companyBarrier: finalCompanyBarrier,
                userBarrier: finalUserBarrier,
                overrideBarrierValues: finalOverrideBarrierValues
            });

            if (messageCached != null) {
                return messageCached?.data;
            }

            // La REL_6_x.x non supporta companyBarrier, lo mettiamo nella "key"
            // TODO: rimuovere quando la DashboardMS sara' aggiornata
            if (finalCompanyBarrier) {
                const { companyId } = this.#getBarrierValues(finalOverrideBarrierValues);

                finalKey = this.#extendStorageKey({
                    key: finalKey,
                    companyId,
                });
            }

            const message: Message<SerializabledDataType | null> | undefined = await this.#instance?.socket?.sendWithConfirmAsync<
                CrossSiteStorageParamsInterface,
                SerializabledDataType
            >(
                {
                    key: finalKey,
                    domain: finalDomain,
                    tenantBarrier: finalTenantBarrier,
                    enterpriseBarrier: finalEnterpriseBarrier,
                    companyBarrier: finalCompanyBarrier,
                    userBarrier: finalUserBarrier,
                    overrideBarrierValues: finalOverrideBarrierValues
                },
                // Quando si leggono/scrivono messaggi cross site anche se l'operazione è locale può essere necessario diverso tempo per recuperare delle chiavi salvate, dipende da quanto è grosso il dato da passare
                30000,
                MessageType.CrossSiteStorageGet,
            ).catch((err) => {
                return new Message(null);
            });

            await this.#updateCrossSiteStorageCache(
                {
                    key: finalKey,
                    domain: finalDomain,
                    tenantBarrier: finalTenantBarrier,
                    enterpriseBarrier: finalEnterpriseBarrier,
                    companyBarrier: finalCompanyBarrier,
                    userBarrier: finalUserBarrier,
                    overrideBarrierValues: finalOverrideBarrierValues
                },
                message
            );

            return message?.data ?? null;
        } else if (finalDomain != null) {
            const store: SerializabledDataType = await this.#readDomainStorage({
                key: finalKey,
                domain: finalDomain,
                tenantBarrier: finalTenantBarrier,
                enterpriseBarrier: finalEnterpriseBarrier,
                companyBarrier: finalCompanyBarrier,
                userBarrier: finalUserBarrier,
                overrideBarrierValues: finalOverrideBarrierValues,
            });

            return store[finalKey] ?? null;
        } else {
            const {
                tenantId,
            } = this.#getBarrierValues(finalOverrideBarrierValues);

            const calculatedKey = this.#composeStorageKey({
                key: finalKey,
                tenantBarrier: finalTenantBarrier,
                enterpriseBarrier: finalEnterpriseBarrier,
                companyBarrier: finalCompanyBarrier,
                userBarrier: finalUserBarrier,
                overrideBarrierValues: finalOverrideBarrierValues
            });

            if (
                finalTenantBarrier &&
                tenantId > 0
            ) {
                if (this.#instance.mockMode) {
                    const data: string | null = window.localStorage.getItem(calculatedKey);

                    return data
                        ? JSON.parse(data)
                        : null;
                } else {
                    return await this.#instance?.ngf?.getItem(calculatedKey);
                }
            } else {
                if (this.#instance.mockMode) {
                    const data = window.localStorage.getItem(finalKey);

                    return data
                        ? JSON.parse(data)
                        : null;
                } else {
                    return await this.#instance.ngf.getItem(finalKey);
                }
            }
        }
    }

    static async removeStorageItem(
        params: RemoveStorageItemParamsInterface
    ): Promise<boolean>;

    /**
     * @deprecated Dalla versione REL_10.0.x escluso utilizzare "Named Parameters" seguendo RemoveStorageItemParamsInterface.
     */
    static async removeStorageItem(
        key: string,
        domain?: string | null,
        tenantBarrier?: boolean,
        enterpriseBarrier?: boolean,
        userBarrier?: boolean,
        overrideBarrierValues?: OverrideBarrierValuesInterface
    ): Promise<boolean>;

    static async removeStorageItem(
        keyOrOpts: string | RemoveStorageItemParamsInterface,
        domain: string | null = null,
        tenantBarrier: boolean = true,
        enterpriseBarrier: boolean = true,
        userBarrier: boolean = false,
        overrideBarrierValues: OverrideBarrierValuesInterface = {
            tenantBarrierValue: undefined,
            enterpriseBarrierValue: undefined,
            companyBarrierValue: undefined,
            userBarrierValue: undefined
        }
    ): Promise<boolean> {

        // Destructuring sicuro all'inizio con type narrowing
        let finalKey: string;
        let finalDomain = domain ?? null;
        let finalTenantBarrier = tenantBarrier ?? true;
        let finalEnterpriseBarrier = enterpriseBarrier ?? true;
        let finalCompanyBarrier = false;
        let finalUserBarrier = userBarrier ?? false;
        let finalOverrideBarrierValues = overrideBarrierValues ?? {
            tenantBarrierValue: undefined,
            enterpriseBarrierValue: undefined,
            companyBarrierValue: undefined,
            userBarrierValue: undefined
        };

        if (typeof keyOrOpts !== 'string') {
            const opts = keyOrOpts;
            finalKey = opts.key;
            finalDomain = opts.domain ?? finalDomain;
            finalTenantBarrier = opts.tenantBarrier ?? finalTenantBarrier;
            finalEnterpriseBarrier = opts.enterpriseBarrier ?? finalEnterpriseBarrier;
            finalCompanyBarrier = opts.companyBarrier ?? false;
            finalUserBarrier = opts.userBarrier ?? finalUserBarrier;
            finalOverrideBarrierValues = opts.overrideBarrierValues ?? finalOverrideBarrierValues;
        } else {
            finalKey = keyOrOpts;
        }

        if (this.#instance?.inIframe) {
            if (!finalDomain) {
                finalDomain = window.location.hostname;
            }

            // La REL_6_x.x non supporta companyBarrier, lo mettiamo nella "key"
            // TODO: rimuovere quando la DashboardMS sara' aggiornata
            if (finalCompanyBarrier) {
                const { companyId } = this.#getBarrierValues(finalOverrideBarrierValues);

                finalKey = this.#extendStorageKey({
                    key: finalKey,
                    companyId,
                });
            }

            const message: Message<boolean | null> | undefined = await this.#instance?.socket?.sendWithConfirmAsync<
                CrossSiteStorageParamsInterface,
                boolean
            >(
                {
                    key: finalKey,
                    domain: finalDomain,
                    tenantBarrier: finalTenantBarrier,
                    enterpriseBarrier: finalEnterpriseBarrier,
                    companyBarrier: finalCompanyBarrier,
                    userBarrier: finalUserBarrier,
                    overrideBarrierValues: finalOverrideBarrierValues
                },
                undefined,
                MessageType.CrossSiteStorageRemove
            ).catch((err) => {
                return new Message(false);
            });

            let result = message?.data ?? false;

            if (result) {
                result = this.#removeCrossSiteStorageCache({
                    key: finalKey,
                    domain: finalDomain,
                    tenantBarrier: finalTenantBarrier,
                    enterpriseBarrier: finalEnterpriseBarrier,
                    companyBarrier: finalCompanyBarrier,
                    userBarrier: finalUserBarrier,
                    overrideBarrierValues: finalOverrideBarrierValues
                });
            }

            return result;
        } else if (finalDomain != null) {
            const store: SerializabledDataType = await this.#readDomainStorage({
                key: finalKey,
                domain: finalDomain,
                tenantBarrier: finalTenantBarrier,
                enterpriseBarrier: finalEnterpriseBarrier,
                companyBarrier: finalCompanyBarrier,
                userBarrier: finalUserBarrier,
                overrideBarrierValues: finalOverrideBarrierValues,
            });

            if ((store)[finalKey]) {
                delete (store)[finalKey];
            }

            return await this.#writeDomainStorage({
                domain: finalDomain,
                store,
                tenantBarrier: finalTenantBarrier,
                enterpriseBarrier: finalEnterpriseBarrier,
                companyBarrier: finalCompanyBarrier,
                userBarrier: finalUserBarrier,
                overrideBarrierValues: finalOverrideBarrierValues
            });
        } else {
            let result = true;

            const {
                tenantId,
            } = this.#getBarrierValues(finalOverrideBarrierValues);

            const calculatedKey = this.#composeStorageKey({
                key: finalKey,
                tenantBarrier: finalTenantBarrier,
                enterpriseBarrier: finalEnterpriseBarrier,
                companyBarrier: finalCompanyBarrier,
                userBarrier: finalUserBarrier,
                overrideBarrierValues: finalOverrideBarrierValues
            });

            if (
                finalTenantBarrier &&
                tenantId > 0
            ) {
                if (this.#instance.mockMode) {
                    window.localStorage.removeItem(calculatedKey);
                } else {
                    await this.#instance.ngf.removeItem(calculatedKey).catch((err) => result = false);
                }
            } else {
                if (this.#instance.mockMode) {
                    window.localStorage.removeItem(finalKey);
                } else {
                    await this.#instance.ngf.removeItem(finalKey).catch((err) => result = false);
                }
            }

            return result;
        }
    }

    // avvia listener per iframe interni
    static startListener(targetWindow: Window) {
        // TODO errore se prima non si ha fatto l'init

        if (this.#instance.socketListener) {
            this.#instance.socketListener.destroy();
        }

        this.#instance.socketListener = new TabSocket(this.#instance.zone, targetWindow, 'storage');
        this.#instance.socketListener.onClose.pipe().subscribe(() => {
            this.#instance?.socketListener?.destroy();
        });

        this.#instance.socketListener.listen<any>(async (message) => {
            const commandMessageAction = (data: any, messageType = message.type) => {
                this.#instance?.socketListener?.send(data, message.uuid, messageType);
            };

            switch (message.type) {
                case MessageType.CrossSiteStorageGet: {
                    const crossSiteStorageGetRequest: CrossSiteStorageParamsInterface = message.data;

                    const getResult = await this.getStorageItem({
                        key: crossSiteStorageGetRequest.key,
                        domain: crossSiteStorageGetRequest.domain,
                        tenantBarrier: crossSiteStorageGetRequest.tenantBarrier,
                        enterpriseBarrier: crossSiteStorageGetRequest.enterpriseBarrier,
                        companyBarrier: crossSiteStorageGetRequest.companyBarrier,
                        userBarrier: crossSiteStorageGetRequest.userBarrier,
                        overrideBarrierValues: crossSiteStorageGetRequest.overrideBarrierValues
                    });

                    commandMessageAction(getResult, MessageType.CrossSiteStorageGet);

                    break;
                }

                case MessageType.CrossSiteStorageSet: {
                    const crossSiteStorageSetRequest: SetCrossSiteStorageParamsInterface<SerializabledDataType> = message.data;

                    const setResult = await this.setStorageItem({
                        key: crossSiteStorageSetRequest.key,
                        value: crossSiteStorageSetRequest.value,
                        domain: crossSiteStorageSetRequest.domain,
                        tenantBarrier: crossSiteStorageSetRequest.tenantBarrier,
                        enterpriseBarrier: crossSiteStorageSetRequest.enterpriseBarrier,
                        userBarrier: crossSiteStorageSetRequest.userBarrier,
                        overrideBarrierValues: crossSiteStorageSetRequest.overrideBarrierValues
                    });

                    commandMessageAction(setResult, MessageType.CrossSiteStorageSet);

                    break;
                }

                case MessageType.CrossSiteStorageRemove: {
                    const crossSiteStorageRemoveRequest: CrossSiteStorageParamsInterface = message.data;

                    const removeResult = await this.removeStorageItem({
                        key: crossSiteStorageRemoveRequest.key,
                        domain: crossSiteStorageRemoveRequest.domain,
                        tenantBarrier: crossSiteStorageRemoveRequest.tenantBarrier,
                        enterpriseBarrier: crossSiteStorageRemoveRequest.enterpriseBarrier,
                        companyBarrier: crossSiteStorageRemoveRequest.companyBarrier,
                        userBarrier: crossSiteStorageRemoveRequest.userBarrier,
                        overrideBarrierValues: crossSiteStorageRemoveRequest.overrideBarrierValues
                    });

                    commandMessageAction(removeResult, MessageType.CrossSiteStorageRemove);

                    break;
                }
            }
        });
    }

    static #getBarrierValues(overrideBarrierValues?: OverrideBarrierValuesInterface): BarrierValuesInterface {
        const result = {
            tenantId: this.#instance?.tenantId,
            enterpriseId: this.#instance?.enterpriseId,
            companyId: this.#instance?.companyId,
            userId: this.#instance?.userId,
        } as BarrierValuesInterface;

        if (overrideBarrierValues?.tenantBarrierValue > 0) {
            result.tenantId = overrideBarrierValues?.tenantBarrierValue;
        };

        if (overrideBarrierValues?.enterpriseBarrierValue > 0) {
            result.enterpriseId = overrideBarrierValues?.enterpriseBarrierValue;
        };

        if (overrideBarrierValues?.companyBarrierValue > 0) {
            result.companyId = overrideBarrierValues?.companyBarrierValue;
        };

        if (overrideBarrierValues?.userBarrierValue > 0) {
            result.userId = overrideBarrierValues?.userBarrierValue;
        };

        return result;
    }

    static async #writeDomainStorage(
        params: WriteDomainStorageParamsInterface
    ): Promise<boolean> {
        let key: string = this.#getDomainStoreKey(params.domain);

        key = this.#composeStorageKey({
            key: key,
            tenantBarrier: params.tenantBarrier,
            enterpriseBarrier: params.enterpriseBarrier,
            companyBarrier: params.companyBarrier,
            userBarrier: params.userBarrier,
            overrideBarrierValues: params.overrideBarrierValues,
        });

        let result = true;

        await this.#instance.ngf.setItem(key, params.store).catch((err) => result = false);

        return result;
    }

    static async #readDomainStorage(
        params: SetStorageItemParamsInterface
    ): Promise<SerializabledDataType> {
        let key: string = this.#getDomainStoreKey(params.domain);

        key = this.#composeStorageKey({
            key: key,
            tenantBarrier: params.tenantBarrier,
            enterpriseBarrier: params.enterpriseBarrier,
            companyBarrier: params.companyBarrier,
            userBarrier: params.userBarrier,
            overrideBarrierValues: params.overrideBarrierValues,
        });

        const store: SerializabledDataType | null = await this.#instance.ngf.getItem(key);

        return store ?? {};
    }

    static #getDomainStoreKey(domain: string) {
        return `crossDomainStore::${domain}`;
    }

    // cache in memoria del browser utilizzata per evitare di fare troppe richieste cross site in un arco temporale molto ristretto.
    // La cache non ha scadenza e non è persistita
    static #crossSiteStorageCache = new Map<string, { message: Message<SerializabledDataType | null> | undefined, creationDate: Date; }>();

    static async #updateCrossSiteStorageCache(
        params: CrossSiteStorageParamsInterface,
        message: MessageInterface<SerializabledDataType | null> | undefined
    ): Promise<void> {
        const key = this.#composeCrossSiteStorageKey(params);

        this.#crossSiteStorageCache.set(
            key,
            {
                message,
                creationDate: new Date()
            }
        );
        return Promise.resolve();
    }

    static async #getCrossSiteStorageCache(
        params: CrossSiteStorageParamsInterface,
    ): Promise<Message<SerializabledDataType | null> | undefined> {
        const key = this.#composeCrossSiteStorageKey(params);

        const cache = this.#crossSiteStorageCache.get(key);

        if (cache != null) {
            return Promise.resolve(cache.message);
        }

        return undefined;
    }

    static #removeCrossSiteStorageCache(
        params: CrossSiteStorageParamsInterface
    ): boolean {
        const key = this.#composeCrossSiteStorageKey(params);

        return this.#crossSiteStorageCache.delete(key);
    }

    static #composeStorageKey({
        key,
        tenantBarrier,
        enterpriseBarrier,
        companyBarrier,
        userBarrier,
        overrideBarrierValues,
    }): string {
        let result = key;

        const {
            tenantId,
            enterpriseId,
            companyId,
            userId,
        } = this.#getBarrierValues(overrideBarrierValues);

        if (tenantBarrier) {
            result = this.#extendStorageKey({
                key: result,
                tenantId,
            });
        }

        if (this.#instance?.env.isEnterpriseBarrierRequired) {
            if (enterpriseBarrier) {
                result = this.#extendStorageKey({
                    key: result,
                    enterpriseId,
                });
            }

            if (companyBarrier) {
                result = this.#extendStorageKey({
                    key: result,
                    companyId,
                });
            }
        }

        if (userBarrier) {
            result = this.#extendStorageKey({
                key: result,
                userId,
            });
        }

        return result;
    }

    static #composeCrossSiteStorageKey(
        params: CrossSiteStorageParamsInterface
    ) {
        const {
            tenantId,
            enterpriseId,
            companyId,
            userId,
        } = this.#getBarrierValues(params.overrideBarrierValues);

        const result = [
            params.key,
            params.domain,
            params.tenantBarrier,
            params.enterpriseBarrier,
            params.companyBarrier,
            params.userBarrier,
            tenantId ?? 0,
            enterpriseId ?? 0,
            companyId ?? 0,
            userId ?? 0,
        ].join("-");

        return result;
    }

    static #extendStorageKey(
        params: StorageKeyExtensionParams
    ): string {
        let result = params.key;

        if (params.tenantId > 0) {
            result = `${result}_T${params.tenantId}`;
        }

        if (params.enterpriseId > 0) {
            result = `${result}_E${params.enterpriseId}`;
        }

        if (params.companyId > 0) {
            result = `${result}_C${params.companyId}`;
        }

        if (params.userId > 0) {
            result = `${result}_U${params.userId}`;
        }

        return result;
    }
}